import { Fragment } from 'react';
import { Transition } from '@headlessui/react';
import classNames from 'classnames';
import { useHidable } from 'shared/lib/hooks';
import { MaterialIcon } from 'shared/ui/MaterialIcon';
import { Button } from 'shared/ui/Button';
import { isImpersonate } from 'shared/lib/api';
import {
  useGetUser,
  type AuthenticatedContractorModel,
  useUpdateContractor,
  TERMS_CONDITIONS_LINK,
} from 'entities/profile';

import style from './Terms.module.scss';

export function TermsAndConditionPopup() {
  const modal = useHidable(true);
  const personQuery = useGetUser<AuthenticatedContractorModel>();
  const person = personQuery.data?.data;

  const isTermsNotShown =
    personQuery.isLoading || Boolean(person?.lastTermsAndConditions.isAccepted || isImpersonate());

  const updateContractorQuery = useUpdateContractor();

  const handleAccept = () => {
    if (person) {
      updateContractorQuery.mutate({
        payload: {
          tcVersion: person.lastTermsAndConditions.version,
        },
      });
      modal.hide();
    }
  };

  if (isTermsNotShown) {
    return <></>;
  }

  return (
    <Transition
      appear
      show={modal.isShown}
      as={Fragment}
    >
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <div
          className={classNames(
            style.terms,
            'mb-10 h-[174px] w-[768px] overflow-hidden rounded-md border border-deep-blue-100 bg-white p-4 text-left align-middle transition-all'
          )}
        >
          <div className="flex gap-4">
            <MaterialIcon className="h-[24px] w-[24px] text-deep-blue-500">announcement</MaterialIcon>
            <div>
              <h3 className="text-sm font-medium leading-6 text-slate-900">Important: Terms & Conditions</h3>
              <div className="mt-2">
                <p className="text-sm font-normal text-slate-700">
                  We've established our{' '}
                  <a
                    target="_blank"
                    href={TERMS_CONDITIONS_LINK}
                    className="border-b font-semibold text-deep-blue-500"
                    rel="noreferrer"
                  >
                    Terms & Conditions
                  </a>
                  , effective March 8, 2024. By continuing to use our service after this date, you agree to these terms.
                  If you have any questions about the{' '}
                  <a
                    target="_blank"
                    href={TERMS_CONDITIONS_LINK}
                    className="border-b font-semibold text-deep-blue-500"
                    rel="noreferrer"
                  >
                    Terms & Conditions
                  </a>
                  , please contact your partnerships manager to discuss further details.
                </p>
              </div>
              <div className="mt-4">
                <Button
                  type="button"
                  size="small"
                  variant="outline"
                  className="bg-blue-100 text-blue-900 hover:bg-blue-200 focus-visible:ring-blue-500 inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  onClick={handleAccept}
                >
                  I understand
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Transition.Child>
    </Transition>
  );
}
